import { Link } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  FileInput,
  FileOutput,
  ChevronRight,
} from 'lucide-react';

interface SeriesDay {
  day: number;
  letterInId: string;
  letterOutId: string | null;
  status: string;
  sentAt: string | null;
  receivedAt: string;
}

interface SeriesTimelineProps {
  total: number;
  completed: number;
  nextDay: number | null;
  days: SeriesDay[];
  expanded?: boolean;
  onToggleExpand?: () => void;
}

function DayStatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'sent':
    case 'queued':
      return <CheckCircle size={14} className="text-success" />;
    case 'generating':
      return <Loader2 size={14} className="text-rune animate-spin" />;
    case 'failed':
      return <XCircle size={14} className="text-ember" />;
    default:
      return <Clock size={14} className="text-mist/50" />;
  }
}

export function SeriesTimeline({
  total,
  completed,
  nextDay,
  days,
  expanded = false,
  onToggleExpand,
}: SeriesTimelineProps) {
  const progress = Math.min(100, (completed / total) * 100);
  const isComplete = completed >= total;

  return (
    <div className="bg-void/50 rounded-lg overflow-hidden">
      {/* Header with progress */}
      <button
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between p-3 hover:bg-stone/10 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="text-mist text-xs font-medium">Series Progress</div>
          <div className="flex items-center gap-1.5">
            {/* Mini day indicators */}
            {days.slice(0, 10).map((day) => (
              <div
                key={day.day}
                className={`w-2 h-2 rounded-full ${
                  day.status === 'sent' || day.status === 'queued'
                    ? 'bg-success'
                    : day.status === 'generating'
                      ? 'bg-rune animate-pulse'
                      : day.status === 'failed'
                        ? 'bg-ember'
                        : 'bg-stone/40'
                }`}
                title={`Day ${day.day}: ${day.status}`}
              />
            ))}
            {total > 10 && <span className="text-mist/50 text-[10px]">+{total - 10}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-xs font-medium ${isComplete ? 'text-success' : 'text-parchment'}`}
          >
            {completed}/{total}
          </span>
          <ChevronRight
            size={14}
            className={`text-mist transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        </div>
      </button>

      {/* Progress bar */}
      <div className="px-3 pb-3">
        <div className="bg-stone/30 rounded-full h-1.5 overflow-hidden">
          <div
            className={`h-1.5 rounded-full transition-all duration-500 ${
              isComplete ? 'bg-success' : 'bg-rune'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Expanded day list */}
      {expanded && (
        <div className="border-t border-stone/20 max-h-64 overflow-y-auto">
          {days.map((day, idx) => (
            <div
              key={day.day}
              className={`flex items-center gap-3 px-3 py-2 ${
                idx !== days.length - 1 ? 'border-b border-stone/10' : ''
              } ${day.day === nextDay ? 'bg-rune/5' : ''}`}
            >
              {/* Day number */}
              <div className="w-8 text-center">
                <span
                  className={`text-xs font-mono ${
                    day.status === 'sent' || day.status === 'queued'
                      ? 'text-success'
                      : day.status === 'generating'
                        ? 'text-rune'
                        : 'text-mist'
                  }`}
                >
                  {day.day}
                </span>
              </div>

              {/* Status */}
              <DayStatusIcon status={day.status} />

              {/* Status text */}
              <span className="flex-1 text-xs text-mist capitalize">{day.status}</span>

              {/* Links */}
              <div className="flex items-center gap-1">
                <Link
                  to={`/admin/letters-in/${day.letterInId}`}
                  className="p-1 text-mist/50 hover:text-rune transition-colors"
                  title="View input letter"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FileInput size={12} />
                </Link>
                {day.letterOutId ? (
                  <Link
                    to={`/admin/letters-out/${day.letterOutId}`}
                    className="p-1 text-mist/50 hover:text-rune transition-colors"
                    title="View output letter"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FileOutput size={12} />
                  </Link>
                ) : (
                  <span className="p-1 text-stone/30">
                    <FileOutput size={12} />
                  </span>
                )}
              </div>

              {/* Timestamp */}
              {day.sentAt && (
                <span className="text-[10px] text-mist/40">
                  {new Date(day.sentAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
